import {
    ExecuteActionRequest,
    GameState,
    HotbarClickRequest,
    InputFieldRequest,
    setInventoryRequest, setPanicPoints,
    TabletFinishRequest,
    changeRoom, setWaterState
} from "@shared/types";
import { BaseRouteService } from "./BaseRouteService";

/**
 * Service to communicate with game routes of the server application
 */
export class GameRouteService extends BaseRouteService {
    /**
     * Get a type of game state for the current player from the server application
     *
     * @returns A type of game state for the current player
     */
    public async getGameState(): Promise<GameState> {
        return this.getJsonApi<GameState>("game/state");
    }

    /**
     * Execute an action for the current player on the server application
     *
     * @param actionAlias Alias of the action to executejsonapi
     * @param objectAliases Aliases of the game objects to execute the action on
     *
     * @returns A type of game state, or `undefined` if the action was unhandled. This can indicate the action requires a second game object to work.
     */
    public async executeAction(
        actionAlias: string,
        objectAliases?: string[]
    ): Promise<GameState | undefined> {
        try {
            return await this.postJsonApi<GameState, ExecuteActionRequest>("game/action", {
                action: actionAlias,
                objects: objectAliases,
            });
        }
        catch {
            return undefined;
        }
    }

    /**
     * Posts to the `connected` endpoint on initial page load.
     */
    public connected(): void {
        void this.postJsonApi("game/connected");
    }

    /**
     * @param input The code that has been entered by the user.
     */
    public inputFieldInput(input: string): Promise<GameState | undefined> {
        return this.postJsonApi<GameState, InputFieldRequest>("game/field-input", {
            input: input,
        });
    }

    /**
     * @param victory The state of the tablet puzzle.
     */
    public finishedTablet(victory: boolean): Promise<GameState | undefined> {
        return this.postJsonApi<GameState, TabletFinishRequest>("game/finished-tablet", {
            finishedTablet: victory,
        });
    }

    /**
     * @param update the state of the inventory.
     */
    public updateInventory(inventory: string[]): Promise<GameState | undefined> {
        return this.postJsonApi<GameState, setInventoryRequest>("game/set-inventory", {
            inventory: inventory,
        });
    }

    /**
     * @param update the state of the water.
     */
    public changeWaterState(drankWater: boolean): Promise<GameState | undefined> {
        return this.postJsonApi<GameState, setWaterState>("game/set-water-state", {
            drankWater: drankWater,
        });
    }

    /**
     * @param update the state of the panic points.
     */
    public updatePanicPoint(panicPoints: number): Promise<GameState | undefined> {
        return this.postJsonApi<GameState, setPanicPoints>("game/set-panicpoints", {
            panicPoints: panicPoints,
        });
    }

    /**
     * @param Change the room to the game over screen
     */
    public changeRoom(currentRoom: string): Promise<GameState | undefined> {
        return this.postJsonApi<GameState, changeRoom>("game/change-room", {
            currentRoom: currentRoom,
        });
    }

    /**
     * Posts a hotbar click to the backend.
     *
     * @param index The 0-indexed number of the hotbar slot that has been clicked.
     * @return The updated game state, or {@code undefined} if this request was not valid (i.e. a bad index, empty slot).
     */
    public hotbarClicked(index: number): Promise<GameState | undefined> {
        return this.postJsonApi<GameState, HotbarClickRequest>("game/hotbar-click", {
            slot: index,
        });
    }
}
